import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { X, Gift, Users, Copy, MessageCircle, Sparkles } from "lucide-react";
import { toast } from "sonner";

const SEEN_KEY = "eventom_invite_nudge_seen";

/**
 * OnboardingInviteNudge — shows once, ~4 s after a first-time visitor picks
 * their city.  Frames the referral system as "Invite 2 friends → unlock a
 * free ticket".  Logged-in users see their real code; anonymous users see a
 * generic share prompt with a signup CTA.
 */
export default function OnboardingInviteNudge() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      if (localStorage.getItem(SEEN_KEY)) return;
      const city = localStorage.getItem("eventom_city");
      if (!city) return;
      const t = setTimeout(() => setOpen(true), 3500);
      return () => clearTimeout(t);
    } catch {}
  }, []);

  const close = () => {
    setOpen(false);
    try { localStorage.setItem(SEEN_KEY, "1"); } catch {}
  };

  if (!open) return null;

  const code = user?.referral_code;
  const link = code ? `https://eventom.online/signup?ref=${code}` : "https://eventom.online/signup";
  const waText = encodeURIComponent(`Hey! Join me on Eventom — India's live culture app. Events, contests, VIP, matchmaking + free ticket unlock 🎟️ ${link}`);

  const copy = async () => {
    try { await navigator.clipboard.writeText(link); toast.success("Link copied — share with 2 friends"); }
    catch { toast.error("Couldn't copy — long-press to copy manually"); }
  };

  return createPortal(
    <div className="fixed bottom-24 lg:bottom-6 right-4 z-[58] w-[calc(100%-2rem)] max-w-sm animate-[slideUp_.5s_ease]" data-testid="invite-nudge">
      <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-white/20" style={{ background: "linear-gradient(140deg, #FF2E9A 0%, #7B2FFF 55%, #2E6BFF 100%)" }}>
        <button onClick={close} data-testid="invite-nudge-close" className="absolute top-2.5 right-2.5 w-7 h-7 rounded-full bg-black/25 hover:bg-black/40 text-white flex items-center justify-center z-10">
          <X className="w-3.5 h-3.5" />
        </button>

        {/* Grain */}
        <div className="absolute inset-0 opacity-20 mix-blend-overlay pointer-events-none" style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.6'/></svg>\")" }} />

        <div className="relative p-4 text-white">
          <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-black/30 text-[9px] font-mono uppercase tracking-widest">
            <Sparkles className="w-3 h-3" /> Welcome
          </div>
          <h3 className="font-fancy text-2xl leading-tight mt-2">Bring 2 friends. Unlock a <span className="text-lime">free ticket.</span></h3>

          <div className="mt-3 flex gap-1.5">
            <a href={`https://wa.me/?text=${waText}`} target="_blank" rel="noreferrer noopener" data-testid="invite-nudge-wa"
              className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-full bg-lime text-ink font-heading font-bold text-[10px] uppercase tracking-widest">
              <MessageCircle className="w-3 h-3" /> WhatsApp
            </a>
            <button onClick={copy} data-testid="invite-nudge-copy" className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-full bg-white/15 backdrop-blur border border-white/25 text-white font-heading font-bold text-[10px] uppercase tracking-widest">
              <Copy className="w-3 h-3" /> Copy link
            </button>
          </div>
          {!user && (
            <Link to="/signup" onClick={close} data-testid="invite-nudge-signup" className="block w-full py-1.5 mt-1.5 text-[9px] font-mono uppercase tracking-widest text-white/80 hover:text-white underline text-center">
              Sign up for your personal code →
            </Link>
          )}

          {code && (
            <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-black/25 border border-white/20 text-[10px] font-mono" data-testid="invite-nudge-code">
              <Gift className="w-3 h-3 text-lime" /> <span className="font-bold text-lime">{code}</span>
            </div>
          )}
        </div>
      </div>
      <style>{`@keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }`}</style>
    </div>,
    document.body
  );
}
